import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  keywords?: string;
  noIndex?: boolean;
  schemaType?: 'Organization' | 'WebSite' | 'WebPage' | 'Article' | 'SoftwareApplication';
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl = 'https://rythm.fm/',
  keywords = 'music app, social music, discord bot, music discovery',
  noIndex = false,
  schemaType = 'WebSite'
}) => {
  // Ensure title has the brand name if it doesn't already
  const fullTitle = title.includes('Rythm') ? title : `${title} | Rythm`;
  
  // Basic schema.org structured data for the website
  const getJsonLd = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      url: canonicalUrl,
      name: fullTitle,
      description: description,
      image: 'https://rythm.fm/assets/images/og.gif'
    };

    // Add specific properties based on schema type
    if (schemaType === 'Organization') {
      return {
        ...baseSchema,
        logo: 'https://rythm.fm/assets/images/logo.png',
        sameAs: [
          'https://twitter.com/rythmfm',
          'https://www.instagram.com/rythmfm/'
        ]
      };
    } else if (schemaType === 'SoftwareApplication') {
      return {
        ...baseSchema,
        applicationCategory: 'MusicApplication',
        operatingSystem: 'iOS, Android',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD'
        }
      };
    }

    return baseSchema;
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots Meta Tag */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getJsonLd())}
      </script>
    </Helmet>
  );
};

export default SEO; 